// Deterministic core for Julio's chart proposals.
//
// Julio emits a strict CHART CONFIG – never data. This module validates that
// config and, in the browser (where the real calculated employees live), turns it
// into the exact row shapes the app's existing chart components consume, with an
// `employeeIds` array on every datum so the same click/lasso → reveal-identifiers
// drill-in works. The model only ever sees the config plus aggregate cell counts;
// raw rows and ids never leave the browser.
//
// Privacy guard: an aggregated chart (bar/pie) must never render a cell that
// re-identifies a small group, so cells below MIN_CELL are merged into "Other"
// and a too-small "Other" is dropped. A scatter plots one point per employee, so
// it is only allowed once at least MIN_CELL employees are in scope (identity then
// stays behind the dialog's reveal-identifiers gate, as elsewhere in the app).

import { CALCULATED_EMPLOYEE_FIELDS, FILTER_OPERATORS } from './fieldCatalog.js'

export const CHART_TYPES = ['bar', 'scatter', 'pie']
export const CHART_AGGREGATES = ['count', 'sum', 'average']

// Below this many employees a visible cell (or a whole scatter) can re-identify a
// person, so the guard merges or blocks it.
export const MIN_CELL = 4
const MAX_BINS = 6

const isNumericField = (field) => CALCULATED_EMPLOYEE_FIELDS[field]?.type === 'number'

// One accessor so every part of the builder reads a field the same way the tools
// do (eligibilityStatus lives under employee.eligibility.status).
function fieldValue(employee, field) {
  if (field === 'eligibilityStatus') return employee.eligibility?.status ?? 'unknown'
  return employee[field]
}

function labelForCategory(field, value) {
  const meta = CALCULATED_EMPLOYEE_FIELDS[field]
  if (meta?.type === 'boolean') {
    if (field === 'isHCE') return value ? 'HCE' : 'NHCE'
    return value ? 'Yes' : 'No'
  }
  if (value === '' || value === null || value === undefined) return 'Unknown'
  return String(value)
}

function passesFilter(employee, filter) {
  const { field, operator = 'eq', value } = filter || {}
  const actual = fieldValue(employee, field)
  if (['lt', 'lte', 'gt', 'gte'].includes(operator)) {
    const left = Number(actual)
    const right = Number(value)
    if (!Number.isFinite(left) || !Number.isFinite(right)) return false
    if (operator === 'lt') return left < right
    if (operator === 'lte') return left <= right
    if (operator === 'gt') return left > right
    return left >= right
  }
  if (operator === 'in') {
    return Array.isArray(value)
      && value.map((item) => String(item).toLowerCase()).includes(String(actual).toLowerCase())
  }
  if (operator === 'contains') return String(actual ?? '').toLowerCase().includes(String(value ?? '').toLowerCase())
  if (operator === 'neq') return String(actual).toLowerCase() !== String(value).toLowerCase()
  if (typeof actual === 'boolean') return actual === Boolean(value)
  return String(actual).toLowerCase() === String(value).toLowerCase()
}

const fieldLabel = (field) => CALCULATED_EMPLOYEE_FIELDS[field]?.label || field
const capitalize = (text) => (text ? text[0].toUpperCase() + text.slice(1) : text)

// A human title describing WHAT the chart shows, derived from the config – used so
// a chart reads "Headcount by gender" rather than "Pie chart". The model may still
// pass its own title; this is the default when it does not.
export function describeChart(config) {
  if (!config || typeof config !== 'object') return 'Chart'
  if (config.chartType === 'scatter') {
    return capitalize(`${fieldLabel(config.y)} vs ${fieldLabel(config.x)}`)
  }
  const group = fieldLabel(config.groupBy)
  const aggregate = config.aggregate || 'count'
  if (aggregate === 'count') {
    return config.chartType === 'pie'
      ? capitalize(`${group} breakdown`)
      : capitalize(`Headcount by ${group}`)
  }
  const verb = aggregate === 'sum' ? 'Total' : 'Average'
  return capitalize(`${verb} ${fieldLabel(config.metric)} by ${group}`)
}

// Validate the config the model emits. Unknown fields, wrong field types for the
// chosen axis, or an unsupported chart type all fail loudly – we never silently
// coerce a bad config into a misleading chart.
export function validateChartConfig(config) {
  const errors = []
  if (!config || typeof config !== 'object' || Array.isArray(config)) {
    return { valid: false, errors: ['A chart config object is required.'] }
  }
  if (!CHART_TYPES.includes(config.chartType)) {
    errors.push(`chartType must be one of ${CHART_TYPES.join(', ')}.`)
  }
  if (!['A', 'B'].includes(config.plan)) errors.push('plan must be A or B.')

  const requireField = (key, kind) => {
    const field = config[key]
    if (!field || !CALCULATED_EMPLOYEE_FIELDS[field]) {
      errors.push(`${key} must be a known employee field.`)
      return
    }
    if (kind === 'numeric' && !isNumericField(field)) errors.push(`${key} must be a numeric field.`)
  }

  if (config.chartType === 'scatter') {
    requireField('x', 'numeric')
    requireField('y', 'numeric')
    if (config.series && !CALCULATED_EMPLOYEE_FIELDS[config.series]) {
      errors.push('series must be a known employee field.')
    }
  } else if (config.chartType === 'bar' || config.chartType === 'pie') {
    requireField('groupBy')
    const aggregate = config.aggregate || 'count'
    if (!CHART_AGGREGATES.includes(aggregate)) errors.push(`aggregate must be one of ${CHART_AGGREGATES.join(', ')}.`)
    if (aggregate !== 'count') {
      if (config.chartType === 'pie') errors.push('A pie chart can only show counts; use a bar chart to aggregate a metric.')
      requireField('metric', 'numeric')
    }
  }

  for (const filter of config.filters || []) {
    if (!filter || !CALCULATED_EMPLOYEE_FIELDS[filter.field]) errors.push('Each filter must target a known employee field.')
    else if (filter.operator && !FILTER_OPERATORS.includes(filter.operator)) errors.push(`Unsupported filter operator: ${filter.operator}.`)
  }

  return { valid: errors.length === 0, errors }
}

// Evenly spaced numeric bins with rounded edges, labeled like "40–60" or "$40K–$60K".
function buildBins(values, field) {
  const finite = values.filter((value) => Number.isFinite(value))
  if (!finite.length) return []
  const min = Math.min(...finite)
  const max = Math.max(...finite)
  if (min === max) return [{ lo: min, hi: max, label: formatEdge(min, field) }]
  const binCount = Math.min(MAX_BINS, Math.max(2, Math.ceil(Math.sqrt(finite.length))))
  const step = (max - min) / binCount
  const bins = []
  for (let i = 0; i < binCount; i += 1) {
    const lo = min + step * i
    const hi = i === binCount - 1 ? max : min + step * (i + 1)
    bins.push({ lo, hi, label: `${formatEdge(lo, field)}–${formatEdge(hi, field)}`, last: i === binCount - 1 })
  }
  return bins
}

// Format a numeric field value with its unit ($ for dollar fields, % for rate
// fields, K-compacted otherwise). Exported so chart AXIS ticks render with the
// same units as the bin labels and tooltips – a deferral axis reads "8%", a
// compensation axis reads "$120K" – across both the built-in and configured charts.
export function formatFieldValue(field, value) {
  const numeric = Number(value) || 0
  const meta = CALCULATED_EMPLOYEE_FIELDS[field]
  const rounded = Math.abs(numeric) >= 1000 ? `${Math.round(numeric / 1000)}K` : `${Math.round(numeric)}`
  if (field === 'compensation' || /Amount$|Contribution$|balance|employerTotal/.test(field)) return `$${rounded}`
  if (meta?.label?.includes('percentage') || field === 'totalDeferralPercent') return `${Math.round(numeric)}%`
  return rounded
}

const formatEdge = (value, field) => formatFieldValue(field, value)

// Group filtered employees into labeled cells, then apply the privacy guard.
function buildCells(employees, config) {
  const field = config.groupBy
  const aggregate = config.aggregate || 'count'
  const metric = config.metric
  const cells = new Map()

  const push = (key, label, employee) => {
    if (!cells.has(key)) cells.set(key, { key, label, employeeIds: [], values: [] })
    const cell = cells.get(key)
    cell.employeeIds.push(employee.id)
    if (aggregate !== 'count') cell.values.push(Number(fieldValue(employee, metric)) || 0)
  }

  if (isNumericField(field)) {
    const bins = buildBins(employees.map((e) => Number(fieldValue(e, field))), field)
    for (const employee of employees) {
      const value = Number(fieldValue(employee, field))
      const bin = bins.find((b) => (b.last ? value <= b.hi : value < b.hi) && value >= b.lo) || bins.at(-1)
      if (bin) push(bin.label, bin.label, employee)
    }
  } else {
    for (const employee of employees) {
      const raw = fieldValue(employee, field)
      push(String(raw ?? 'unknown'), labelForCategory(field, raw), employee)
    }
  }

  const finalize = (cell) => ({
    key: cell.key,
    label: cell.label,
    employeeIds: cell.employeeIds,
    count: cell.employeeIds.length,
    value: aggregate === 'count'
      ? cell.employeeIds.length
      : aggregate === 'sum'
        ? cell.values.reduce((s, v) => s + v, 0)
        : cell.values.length ? cell.values.reduce((s, v) => s + v, 0) / cell.values.length : 0,
  })

  const ordered = [...cells.values()].map(finalize)
  return applyCellGuard(ordered, aggregate)
}

// Merge cells smaller than MIN_CELL into one "Other" cell; drop a residual "Other"
// that is itself too small. Guarantees every rendered cell maps to >= MIN_CELL
// employees (or is absent), so no slice/bar re-identifies a small group.
function applyCellGuard(cells, aggregate) {
  const safe = cells.filter((cell) => cell.count >= MIN_CELL)
  const small = cells.filter((cell) => cell.count < MIN_CELL)
  let suppressedCount = 0
  if (small.length) {
    const mergedIds = small.flatMap((cell) => cell.employeeIds)
    if (mergedIds.length >= MIN_CELL) {
      safe.push({
        key: '__other__',
        label: `Other (${small.length} small group${small.length === 1 ? '' : 's'})`,
        employeeIds: mergedIds,
        count: mergedIds.length,
        // A merged bucket's value is only meaningful as a count; never average
        // across heterogeneous small groups.
        value: aggregate === 'count' ? mergedIds.length : mergedIds.length,
      })
    } else {
      suppressedCount = mergedIds.length
    }
  }
  return { cells: safe, suppressedCount }
}

// The public builder. Returns chart rows in the app's existing shapes plus a render
// descriptor and aggregate meta. Throws when the guard leaves nothing safe to show.
export function buildChartData(config, calculatedEmployees) {
  const validation = validateChartConfig(config)
  if (!validation.valid) throw new Error(validation.errors.join(' '))

  const employees = (calculatedEmployees || []).filter((employee) => (config.filters || []).every((f) => passesFilter(employee, f)))
  if (!employees.length) throw new Error('No employees match these filters, so there is nothing to chart.')

  if (config.chartType === 'scatter') {
    if (employees.length < MIN_CELL) {
      throw new Error(`Only ${employees.length} employees are in scope; a scatter needs at least ${MIN_CELL} to avoid identifying individuals. Add or widen filters.`)
    }
    const seriesField = config.series || 'isHCE'
    const data = employees.map((employee) => ({
      id: employee.id,
      [config.x]: Number(fieldValue(employee, config.x)) || 0,
      [config.y]: Number(fieldValue(employee, config.y)) || 0,
      seriesLabel: labelForCategory(seriesField, fieldValue(employee, seriesField)),
      isHCE: Boolean(employee.isHCE),
    }))
    const seriesValues = [...new Set(data.map((d) => d.seriesLabel))]
    return {
      chartType: 'scatter',
      data,
      scatterDataSets: seriesValues.map((label) => data.filter((d) => d.seriesLabel === label)),
      render: { xKey: config.x, yKey: config.y, seriesField, seriesValues, xLabel: CALCULATED_EMPLOYEE_FIELDS[config.x].label, yLabel: CALCULATED_EMPLOYEE_FIELDS[config.y].label },
      meta: { totalPlotted: data.length, suppressedCount: 0 },
    }
  }

  const { cells, suppressedCount } = buildCells(employees, config)
  if (!cells.length) {
    throw new Error('Every group here is too small to chart without identifying individuals. Broaden the grouping or filters.')
  }
  const data = cells.map((cell) => ({ label: cell.label, value: cell.value, count: cell.count, name: cell.label, employeeIds: cell.employeeIds }))
  return {
    chartType: config.chartType,
    data,
    render: {
      xKey: 'label',
      valueKey: config.chartType === 'pie' ? 'value' : (config.aggregate && config.aggregate !== 'count' ? 'value' : 'count'),
      aggregate: config.aggregate || 'count',
      metric: config.metric || null,
      groupBy: config.groupBy || null,
    },
    meta: { cells: cells.map((c) => ({ label: c.label, count: c.count, value: c.value })), suppressedCount },
  }
}
